import { apiClient } from './../client';
import type { IApiResponse } from './../types';
import type {
    IAgencyBranding,
    IAgencyActivity,
    IAgencyClient,
    IAgencyMe,
    IAgencyOverview,
    IAgencyPlanPricingRule,
    IAgencySetup,
    ICreateClientInput,
    ICreateSetupInput,
    IClientRebill,
    IOnboardingChecklist,
    IResolvedBranding,
    ISetupInstallReport,
    IStripeConnection,
    ISetupUpdatePreview,
    IUpdateBrandingInput,
    IUpdateClientPricingInput,
} from '@/features/agency/types';

const BASE = '/agency';

export const agencyApi = {
    /** Null when the caller belongs to no agency — drives the empty state. */
    getMe: () =>
        apiClient.get<IApiResponse<IAgencyMe | null>>(`${BASE}/me`).then((r) => r.data.data),

    create: (input: { name: string }) =>
        apiClient.post<IApiResponse<{ agencyId: string }>>(BASE, input).then((r) => r.data.data),

    getOverview: () =>
        apiClient.get<IApiResponse<IAgencyOverview>>(`${BASE}/overview`).then((r) => r.data.data),

    getClients: () =>
        apiClient
            .get<IApiResponse<readonly IAgencyClient[]>>(`${BASE}/clients`)
            .then((r) => r.data.data),

    createClient: (input: ICreateClientInput) =>
        apiClient
            .post<
                IApiResponse<{ organizationId: string; setupReport?: ISetupInstallReport }>
            >(`${BASE}/clients`, input)
            .then((r) => r.data.data),

    getSetups: () =>
        apiClient
            .get<IApiResponse<readonly IAgencySetup[]>>(`${BASE}/setups`)
            .then((r) => r.data.data),

    createSetup: (input: ICreateSetupInput) =>
        apiClient
            .post<IApiResponse<{ setupId: string }>>(`${BASE}/setups`, input)
            .then((r) => r.data.data),

    duplicateSetup: (setupId: string) =>
        apiClient
            .post<IApiResponse<{ setupId: string }>>(`${BASE}/setups/${setupId}/duplicate`)
            .then((r) => r.data.data),

    updateSetup: (
        setupId: string,
        input: { name?: string; description?: string | null; industry?: string | null },
    ) =>
        apiClient
            .patch<IApiResponse<{ updated: boolean }>>(`${BASE}/setups/${setupId}`, input)
            .then((r) => r.data.data),

    deleteSetup: (setupId: string) =>
        apiClient
            .delete<IApiResponse<{ deleted: boolean }>>(`${BASE}/setups/${setupId}`)
            .then((r) => r.data.data),

    installSetup: (organizationId: string, setupId: string) =>
        apiClient
            .post<
                IApiResponse<ISetupInstallReport>
            >(`${BASE}/clients/${organizationId}/setups/${setupId}/install`)
            .then((r) => r.data.data),

    getPricing: () =>
        apiClient
            .get<IApiResponse<readonly IAgencyPlanPricingRule[]>>(`${BASE}/pricing`)
            .then((r) => r.data.data),

    updatePricing: (rules: readonly IAgencyPlanPricingRule[]) =>
        apiClient
            .put<IApiResponse<{ updated: number }>>(`${BASE}/pricing`, { rules })
            .then((r) => r.data.data),

    updateClientPricing: (organizationId: string, input: IUpdateClientPricingInput) =>
        apiClient
            .put<
                IApiResponse<{ organizationId: string }>
            >(`${BASE}/clients/${organizationId}/pricing`, input)
            .then((r) => r.data.data),

    shareSetup: (setupId: string) =>
        apiClient
            .post<IApiResponse<{ shareCode: string }>>(`${BASE}/setups/${setupId}/share`)
            .then((r) => r.data.data),

    revokeSetupShare: (setupId: string) =>
        apiClient
            .delete<IApiResponse<{ revoked: boolean }>>(`${BASE}/setups/${setupId}/share`)
            .then((r) => r.data.data),

    getSharedSetupPreview: (code: string) =>
        apiClient
            .get<IApiResponse<IAgencySetup>>(`${BASE}/setups/shared/${encodeURIComponent(code)}`)
            .then((r) => r.data.data),

    importSharedSetup: (code: string) =>
        apiClient
            .post<
                IApiResponse<{ setupId: string }>
            >(`${BASE}/setups/shared/${encodeURIComponent(code)}/import`)
            .then((r) => r.data.data),

    getSetupUpdatePreview: (organizationId: string, setupId: string) =>
        apiClient
            .get<
                IApiResponse<ISetupUpdatePreview>
            >(`${BASE}/clients/${organizationId}/setups/${setupId}/update-preview`)
            .then((r) => r.data.data),
    getBranding: () =>
        apiClient.get<IApiResponse<IAgencyBranding>>(`${BASE}/branding`).then((r) => r.data.data),

    updateBranding: (input: IUpdateBrandingInput) =>
        apiClient
            .patch<IApiResponse<IAgencyBranding>>(`${BASE}/branding`, input)
            .then((r) => r.data.data),

    uploadBrandingImage: (kind: 'logo' | 'favicon', file: File) => {
        const form = new FormData();
        form.append('file', file);
        return apiClient
            .post<IApiResponse<IAgencyBranding>>(`${BASE}/branding/${kind}`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            })
            .then((r) => r.data.data);
    },

    setBrandingDomain: (domain: string) =>
        apiClient
            .post<IApiResponse<IAgencyBranding>>(`${BASE}/branding/domain`, { domain })
            .then((r) => r.data.data),

    verifyBrandingDomain: () =>
        apiClient
            .post<IApiResponse<IAgencyBranding>>(`${BASE}/branding/domain/verify`)
            .then((r) => r.data.data),

    removeBrandingDomain: () =>
        apiClient
            .delete<IApiResponse<IAgencyBranding>>(`${BASE}/branding/domain`)
            .then((r) => r.data.data),

    /** §51-resolved branding for the CURRENT workspace (org-scoped). */
    getResolvedBranding: () =>
        apiClient.get<IApiResponse<IResolvedBranding>>('/branding').then((r) => r.data.data),

    /** §60 audit trail — plain-language activity, newest first. */
    getActivity: (limit?: number) =>
        apiClient
            .get<IApiResponse<IAgencyActivity[]>>(`${BASE}/activity`, { params: { limit } })
            .then((r) => r.data.data),

    updateAgencySettings: (input: { defaultMarginPct: number | null }) =>
        apiClient
            .patch<IApiResponse<{ updated: boolean }>>(`${BASE}/settings`, input)
            .then((r) => r.data.data),

    getClientOnboarding: (organizationId: string) =>
        apiClient
            .get<IApiResponse<IOnboardingChecklist>>(`${BASE}/clients/${organizationId}/onboarding`)
            .then((r) => r.data.data),

    toggleOnboardingItem: (organizationId: string, itemKey: string) =>
        apiClient
            .post<
                IApiResponse<IOnboardingChecklist>
            >(`${BASE}/clients/${organizationId}/onboarding/${itemKey}/toggle`)
            .then((r) => r.data.data),

    getStripeConnection: () =>
        apiClient
            .get<IApiResponse<IStripeConnection>>(`${BASE}/billing/stripe`)
            .then((r) => r.data.data),

    connectStripe: (secretKey: string) =>
        apiClient
            .post<IApiResponse<IStripeConnection>>(`${BASE}/billing/stripe`, { secretKey })
            .then((r) => r.data.data),

    disconnectStripe: () =>
        apiClient
            .delete<IApiResponse<{ disconnected: boolean }>>(`${BASE}/billing/stripe`)
            .then((r) => r.data.data),

    getClientRebill: (organizationId: string) =>
        apiClient
            .get<IApiResponse<IClientRebill>>(`${BASE}/billing/clients/${organizationId}`)
            .then((r) => r.data.data),

    startClientBilling: (organizationId: string) =>
        apiClient
            .post<IApiResponse<IClientRebill>>(`${BASE}/billing/clients/${organizationId}/start`)
            .then((r) => r.data.data),

    stopClientBilling: (organizationId: string) =>
        apiClient
            .post<IApiResponse<IClientRebill>>(`${BASE}/billing/clients/${organizationId}/stop`)
            .then((r) => r.data.data),
};
